<template>
  <div class="min-h-screen bg-[#f6f7f9]">

    <!-- Hero -->
    <section class="bg-[#111] text-white py-12 px-6">
      <div class="max-w-screen-xl mx-auto text-center">
        <p class="text-[0.8rem] font-bold text-[#FFD500] uppercase tracking-widest mb-3">
          Officiële dealer
        </p>
        <h1 class="text-white text-[clamp(1.8rem,4vw,2.75rem)] font-black leading-tight mb-3">
          Onze merken
        </h1>
        <p class="text-[#aaa] text-[1rem] max-w-xl mx-auto">
          Wij werken uitsluitend met bewezen A-merken voor zonne-energie, laadpalen en accessoires.
        </p>
      </div>
    </section>

    <!-- Zoekbalk -->
    <div class="max-w-screen-xl mx-auto px-6 py-6">
      <div class="relative max-w-md">
        <i class="bi bi-search absolute left-3.5 top-1/2 -translate-y-1/2 text-[#aaa] text-[0.9rem] pointer-events-none"></i>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Zoek een merk..."
          class="w-full pl-10 pr-10 py-2.5 border border-[#e5e7eb] rounded-xl bg-white text-[0.9rem] outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all"
        >
        <button
          v-if="searchQuery"
          @click="searchQuery = ''"
          class="absolute right-3 top-1/2 -translate-y-1/2 text-[#aaa] hover:text-[#111] transition-colors"
        >
          <i class="bi bi-x-lg text-[0.85rem]"></i>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="max-w-screen-xl mx-auto px-6 pb-16">
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <div v-for="n in 10" :key="n" class="bg-white rounded-2xl border border-[#e9eaec] p-5 animate-pulse">
          <div class="h-14 bg-[#f0f0f0] rounded-lg mb-3"></div>
          <div class="h-3 bg-[#f0f0f0] rounded w-2/3 mx-auto"></div>
        </div>
      </div>
    </div>

    <!-- Merken grid -->
    <div v-else class="max-w-screen-xl mx-auto px-6 pb-16">

      <!-- Resultaat teller -->
      <p class="text-[0.8rem] text-[#aaa] mb-4">
        {{ filteredBrands.length }} {{ filteredBrands.length === 1 ? 'merk' : 'merken' }}
        <span v-if="searchQuery"> voor "<strong class="text-[#111]">{{ searchQuery }}</strong>"</span>
      </p>

      <!-- Grid -->
      <div v-if="filteredBrands.length > 0" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <router-link
          v-for="brand in filteredBrands"
          :key="brand.id"
          :to="`/merk/${brand.slug}`"
          class="group bg-white rounded-2xl border border-[#e9eaec] hover:border-[#FFD500] hover:shadow-md p-5 flex flex-col items-center gap-3 transition-all duration-200 no-underline"
        >
          <!-- Logo -->
          <div class="w-full h-14 flex items-center justify-center">
            <img
              v-if="brand.image?.src"
              :src="brand.image.src"
              :alt="brand.name"
              class="max-h-12 max-w-full object-contain grayscale group-hover:grayscale-0 opacity-70 group-hover:opacity-100 transition-all duration-200"
            >
            <span v-else class="text-[1rem] font-black text-[#ccc] group-hover:text-[#FFD500] transition-colors">
              {{ brand.name.slice(0, 2).toUpperCase() }}
            </span>
          </div>

          <!-- Naam -->
          <p class="text-[0.82rem] font-bold text-[#333] text-center group-hover:text-[#111] transition-colors leading-tight">
            {{ brand.name }}
          </p>

          <!-- Aantal producten -->
          <p v-if="brand.count" class="text-[0.72rem] text-[#bbb] group-hover:text-[#FFD500] transition-colors -mt-1">
            {{ brand.count }} producten
          </p>
        </router-link>
      </div>

      <!-- Geen resultaten -->
      <div v-else class="text-center py-20">
        <i class="bi bi-search text-[2.5rem] text-[#ddd] block mb-4"></i>
        <p class="text-[#888] text-[0.95rem] mb-4">Geen merken gevonden voor "{{ searchQuery }}"</p>
        <button
          @click="searchQuery = ''"
          class="text-[0.85rem] font-semibold text-[#FFD500] hover:underline"
        >
          Wis zoekopdracht
        </button>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'

const loading = ref(true)
const brands = ref([])
const searchQuery = ref('')

const filteredBrands = computed(() => {
  let list = [...brands.value]
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    list = list.filter(b =>
      b.name.toLowerCase().includes(q) ||
      b.description?.toLowerCase().includes(q)
    )
  }
  return list.sort((a, b) => a.name.localeCompare(b.name))
})

const fetchBrands = async () => {
  try {
    loading.value = true
    const res = await fetch('/_brands')
    if (res.ok) brands.value = await res.json()
  } catch (e) {
    console.error('Merken ophalen mislukt:', e)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchBrands()
  document.title = 'Merken | Plug2Charge'
})
</script>
